import { format } from "date-fns";
import {
  Clock,
  DollarSign,
  Edit2,
  FileText,
  Percent,
  Plus,
  Trash2,
  TrendingDown,
  TrendingUp,
  Users,
  XCircle,
} from "lucide-react";
import React from "react";
import { Button } from "@/components/ui/button";
import { useTerminology } from "@/hooks/useTerminology";
import { cn } from "@/lib/utils";

interface AdjustmentCardProps {
  adjustment: any;
  onEdit?: () => void;
  onRemove?: () => void;
  className?: string;
}

export function AdjustmentCard({
  adjustment,
  onEdit,
  onRemove,
  className,
}: AdjustmentCardProps) {
  const { getCashMovementTerms } = useTerminology();
  // Safety check
  if (!adjustment) {
    return null;
  }

  const renderHeadcountAdjustment = () => {
    if (adjustment.type === "hire") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Hire {adjustment.numberOfEmployees} {adjustment.role}
                  {adjustment.numberOfEmployees > 1 ? "s" : ""}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • Starting{" "}
                  {format(adjustment.startDate, "MMM d, yyyy")}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Average Salary:</span>
              <span>
                £
                {adjustment.averageSalary.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Annual Cost:</span>
              <span className="font-medium">
                £
                {adjustment.totalAnnualCost.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-red-50 border border-red-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-red-700 font-medium">Monthly Impact:</span>
              <span className="text-red-700 font-semibold">
                -£
                {adjustment.monthlyCost.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                /month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "remove") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Remove {adjustment.numberOfEmployees} {adjustment.role}
                  {adjustment.numberOfEmployees > 1 ? "s" : ""}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • Ending{" "}
                  {format(adjustment.endDate, "MMM d, yyyy")}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Annual Savings:</span>
              <span className="font-medium text-green-700">
                +£
                {adjustment.totalAnnualSavings.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            {adjustment.totalSeverance > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Severance Cost:</span>
                <span className="text-red-600">
                  -£
                  {adjustment.totalSeverance.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Notice Period:</span>
              <span>
                {adjustment.noticePeriod} month
                {adjustment.noticePeriod > 1 ? "s" : ""} (-£
                {adjustment.noticePeriodCost.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                )
              </span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-green-50 border border-green-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-green-700 font-medium">
                Monthly Savings:
              </span>
              <span className="text-green-700 font-semibold">
                +£
                {adjustment.monthlySavings.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                /month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "adjust-salary") {
      const isIncrease = adjustment.adjustmentPercent > 0;
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  "p-2 rounded-lg",
                  isIncrease ? "bg-red-100" : "bg-green-100",
                )}
              >
                {isIncrease ? (
                  <TrendingUp className="h-4 w-4 text-red-600" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-green-600" />
                )}
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  {isIncrease ? "Increase" : "Decrease"} Salaries by{" "}
                  {Math.abs(adjustment.adjustmentPercent)}%
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.role || "All Roles"} •{" "}
                  {adjustment.entity || "All Entities"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Effective {format(adjustment.effectiveDate, "MMM d, yyyy")}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Affected Employees:</span>
              <span>~{adjustment.estimatedAffectedEmployees} people</span>
            </div>
            {adjustment.reason && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reason:</span>
                <span>{adjustment.reason}</span>
              </div>
            )}
          </div>

          <div
            className={cn(
              "ml-11 p-2 border rounded text-xs",
              isIncrease
                ? "bg-red-50 border-red-200"
                : "bg-green-50 border-green-200",
            )}
          >
            <div className="flex items-center justify-between">
              <span
                className={cn(
                  "font-medium",
                  isIncrease ? "text-red-700" : "text-green-700",
                )}
              >
                Monthly Impact:
              </span>
              <span
                className={cn(
                  "font-semibold",
                  isIncrease ? "text-red-700" : "text-green-700",
                )}
              >
                {isIncrease ? "-" : "+"}£
                {Math.abs(adjustment.monthlyImpact).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                /month
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderRevenueAdjustment = () => {
    if (adjustment.type === "adjust-revenue-percent") {
      const isIncrease = adjustment.adjustmentPercent > 0;
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  "p-2 rounded-lg",
                  isIncrease ? "bg-green-100" : "bg-red-100",
                )}
              >
                <Percent
                  className={cn(
                    "h-4 w-4",
                    isIncrease ? "text-green-600" : "text-red-600",
                  )}
                />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  {isIncrease ? "Increase" : "Decrease"} Revenue by{" "}
                  {Math.abs(adjustment.adjustmentPercent)}%
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity || "All Entities"} • Effective{" "}
                  {format(adjustment.effectiveDate, "MMM d, yyyy")}
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">
                    Reason: {adjustment.reason}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Revenue:</span>
              <span>
                £
                {adjustment.estimatedCurrentRevenue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Annual Change:</span>
              <span
                className={cn(
                  "font-medium",
                  isIncrease ? "text-green-700" : "text-red-700",
                )}
              >
                {isIncrease ? "+" : ""}£
                {adjustment.adjustmentAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          <div
            className={cn(
              "ml-11 p-2 border rounded text-xs",
              isIncrease
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200",
            )}
          >
            <div className="flex items-center justify-between">
              <span
                className={cn(
                  "font-medium",
                  isIncrease ? "text-green-700" : "text-red-700",
                )}
              >
                Monthly Impact:
              </span>
              <span
                className={cn(
                  "font-semibold",
                  isIncrease ? "text-green-700" : "text-red-700",
                )}
              >
                {isIncrease ? "+" : ""}£
                {Math.abs(adjustment.monthlyImpact).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                /month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "delay-invoice") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Delay Invoice {adjustment.invoiceId}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.customer} • £
                  {adjustment.amount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">
                    Reason: {adjustment.reason}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Due:</span>
              <span>
                {format(new Date(adjustment.originalDueDate), "MMM d, yyyy")}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">New Due Date:</span>
              <span>{format(adjustment.newDueDate, "MMM d, yyyy")}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delay:</span>
              <span className="text-orange-700 font-medium">
                {adjustment.delayDays} days
              </span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-orange-700 font-medium">
                Cash Flow Impact:
              </span>
              <span className="text-orange-700 font-semibold">
                £
                {adjustment.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{" "}
                delayed
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "add-contract") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  New Contract: {adjustment.customer}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • £
                  {adjustment.contractValue.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format(adjustment.startDate, "MMM yyyy")} -{" "}
                  {format(adjustment.endDate, "MMM yyyy")}
                </p>
                {adjustment.description && (
                  <p className="text-xs text-muted-foreground">
                    {adjustment.description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Contract Value:</span>
              <span className="font-medium">
                £
                {adjustment.contractValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Duration:</span>
              <span>{adjustment.durationMonths} months</span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-green-50 border border-green-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-green-700 font-medium">
                Monthly Revenue:
              </span>
              <span className="text-green-700 font-semibold">
                +£
                {adjustment.monthlyRevenue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                /month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "change-revenue") {
      const impact = adjustment.netCashImpact ?? 0;
      const isPositive = impact >= 0;
      const percent = adjustment.adjustmentPercent ?? 0;
      const isIncrease = percent >= 0;
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  "p-2 rounded-lg",
                  isIncrease ? "bg-green-100" : "bg-red-100",
                )}
              >
                <Percent
                  className={cn(
                    "h-4 w-4",
                    isIncrease ? "text-green-600" : "text-red-600",
                  )}
                />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  {adjustment.name ||
                    `${isIncrease ? "Increase" : "Decrease"} Revenue by ${Math.abs(percent)}%`}
                </h4>
                {adjustment.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {adjustment.description}
                  </p>
                )}
                {adjustment.effectiveDate && (
                  <p className="text-xs text-muted-foreground">
                    Effective{" "}
                    {format(new Date(adjustment.effectiveDate), "MMM d, yyyy")}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div
            className={cn(
              "ml-11 p-2 border rounded text-xs",
              isPositive
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200",
            )}
          >
            <div className="flex items-center justify-between">
              <span
                className={cn(
                  "font-medium",
                  isPositive ? "text-green-700" : "text-red-700",
                )}
              >
                Revenue Impact:
              </span>
              <span
                className={cn(
                  "font-semibold",
                  isPositive ? "text-green-700" : "text-red-700",
                )}
              >
                {isPositive ? "+" : "-"}£
                {Math.abs(impact).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "cancel-project") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Cancel: {adjustment.projectName}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.customer} • Cancelled{" "}
                  {format(adjustment.cancellationDate, "MMM d, yyyy")}
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">
                    Reason: {adjustment.reason}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Project Value:</span>
              <span>
                £
                {adjustment.projectValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            {adjustment.refundAmount > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Refund ({adjustment.refundPercent}%):
                </span>
                <span className="text-red-600">
                  -£
                  {adjustment.refundAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
          </div>

          <div className="ml-11 p-2 bg-red-50 border border-red-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-red-700 font-medium">
                Net Revenue Loss:
              </span>
              <span className="text-red-700 font-semibold">
                -£
                {adjustment.netRevenueLoss.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderCostAdjustment = () => {
    if (adjustment.type === "reduce-costs") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingDown className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Reduce {adjustment.category} by{" "}
                  {Math.abs(adjustment.reductionPercent)}%
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • Effective{" "}
                  {format(adjustment.effectiveDate, "MMM d, yyyy")}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Current Monthly Cost:
              </span>
              <span>
                £
                {adjustment.estimatedCurrentCost.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Annual Savings:</span>
              <span className="font-medium text-green-700">
                +£
                {adjustment.annualSavings.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-green-50 border border-green-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-green-700 font-medium">
                Monthly Savings:
              </span>
              <span className="text-green-700 font-semibold">
                +£
                {adjustment.monthlySavings.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                /month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "increase-costs") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <TrendingUp className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Increase {adjustment.category} by {adjustment.increasePercent}
                  %
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • Effective{" "}
                  {format(adjustment.effectiveDate, "MMM d, yyyy")}
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">
                    Reason: {adjustment.reason}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Current Monthly Cost:
              </span>
              <span>
                £
                {adjustment.estimatedCurrentCost.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Annual Increase:</span>
              <span className="font-medium text-red-700">
                -£
                {adjustment.annualCostIncrease.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-red-50 border border-red-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-red-700 font-medium">
                Monthly Cost Increase:
              </span>
              <span className="text-red-700 font-semibold">
                -£
                {adjustment.monthlyCostIncrease.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                /month
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "change-costs") {
      const impact = adjustment.netCashImpact ?? 0;
      const percent = adjustment.adjustmentPercent ?? 0;
      const isSaving = impact > 0 || percent < 0;
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  "p-2 rounded-lg",
                  isSaving ? "bg-green-100" : "bg-red-100",
                )}
              >
                <Percent
                  className={cn(
                    "h-4 w-4",
                    isSaving ? "text-green-600" : "text-red-600",
                  )}
                />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  {adjustment.name ||
                    `${isSaving ? "Reduce" : "Increase"} Costs by ${Math.abs(percent)}%`}
                </h4>
                {adjustment.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {adjustment.description}
                  </p>
                )}
                {adjustment.effectiveDate && (
                  <p className="text-xs text-muted-foreground">
                    Effective{" "}
                    {format(new Date(adjustment.effectiveDate), "MMM d, yyyy")}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div
            className={cn(
              "ml-11 p-2 border rounded text-xs",
              isSaving
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200",
            )}
          >
            <div className="flex items-center justify-between">
              <span
                className={cn(
                  "font-medium",
                  isSaving ? "text-green-700" : "text-red-700",
                )}
              >
                Cost Impact:
              </span>
              <span
                className={cn(
                  "font-semibold",
                  isSaving ? "text-green-700" : "text-red-700",
                )}
              >
                {impact >= 0 ? "+" : "-"}£
                {Math.abs(impact).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "delay-payment") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Delay Payment: {adjustment.supplier}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  £
                  {adjustment.amount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Due:</span>
              <span>
                {format(new Date(adjustment.originalDueDate), "MMM d, yyyy")}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">New Due Date:</span>
              <span>{format(adjustment.newDueDate, "MMM d, yyyy")}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Delay:</span>
              <span className="text-blue-700 font-medium">
                {adjustment.delayDays} days
              </span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-blue-700 font-medium">Cash Preserved:</span>
              <span className="text-blue-700 font-semibold">
                £
                {adjustment.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "one-time-expense") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Plus className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  {adjustment.description}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.category} • {adjustment.entity}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format(adjustment.date, "MMM d, yyyy")}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-orange-700 font-medium">
                One-Time Expense:
              </span>
              <span className="text-orange-700 font-semibold">
                -£
                {adjustment.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderProjectAdjustment = () => {
    if (adjustment.type === "add_project") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Plus className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  {adjustment.projectName}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.entity} • £
                  {adjustment.totalValue.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format(adjustment.startDate, "MMM yyyy")} -{" "}
                  {format(adjustment.endDate, "MMM yyyy")} •{" "}
                  {adjustment.billingSchedule}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Duration:</span>
              <span>{adjustment.projectDuration} months</span>
            </div>
            {adjustment.billingSchedule !== "completion" && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Monthly Revenue:</span>
                <span className="font-medium text-blue-700">
                  +£
                  {adjustment.monthlyRevenue.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  /month
                </span>
              </div>
            )}
          </div>

          <div className="ml-11 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-blue-700 font-medium">
                Total Contract Value:
              </span>
              <span className="text-blue-700 font-semibold">
                +£
                {adjustment.totalValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "delay_project") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Delay: {adjustment.projectName}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  +{adjustment.delayWeeks} weeks delay
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">
                    Reason: {adjustment.reason}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Start:</span>
              <span>{format(adjustment.originalStartDate, "MMM d, yyyy")}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">New Start:</span>
              <span className="text-orange-700 font-medium">
                {format(adjustment.newStartDate, "MMM d, yyyy")}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">New End:</span>
              <span>{format(adjustment.newEndDate, "MMM d, yyyy")}</span>
            </div>
          </div>

          <div className="ml-11 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-orange-700 font-medium">
                Revenue Timeline Shift:
              </span>
              <span className="text-orange-700 font-semibold">
                +{adjustment.delayWeeks} weeks
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "change_project_value") {
      const isIncrease = adjustment.revenueDelta >= 0;
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div
                className={cn(
                  "p-2 rounded-lg",
                  isIncrease ? "bg-green-100" : "bg-red-100",
                )}
              >
                <TrendingUp
                  className={cn(
                    "h-4 w-4",
                    isIncrease ? "text-green-600" : "text-red-600",
                  )}
                />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  {adjustment.projectName}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Value change: {isIncrease ? "+" : ""}£
                  {Math.abs(adjustment.revenueDelta).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  ({adjustment.percentChange.toFixed(1)}%)
                </p>
                {adjustment.reason && (
                  <p className="text-xs text-muted-foreground">
                    Reason: {adjustment.reason}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Value:</span>
              <span>
                £
                {adjustment.originalValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">New Value:</span>
              <span className="font-medium">
                £
                {adjustment.newValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Effective:</span>
              <span>{format(adjustment.effectiveDate, "MMM d, yyyy")}</span>
            </div>
          </div>

          <div
            className={cn(
              "ml-11 p-2 border rounded text-xs",
              isIncrease
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200",
            )}
          >
            <div className="flex items-center justify-between">
              <span
                className={cn(
                  "font-medium",
                  isIncrease ? "text-green-700" : "text-red-700",
                )}
              >
                Revenue Delta:
              </span>
              <span
                className={cn(
                  "font-semibold",
                  isIncrease ? "text-green-700" : "text-red-700",
                )}
              >
                {isIncrease ? "+" : ""}£
                {Math.abs(adjustment.revenueDelta).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "cancel_project") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Cancel: {adjustment.projectName}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Cancelled {format(adjustment.cancellationDate, "MMM d, yyyy")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {adjustment.workCompletedPercent.toFixed(0)}% complete
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Original Value:</span>
              <span>
                £
                {adjustment.originalValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Earned Revenue:</span>
              <span className="text-green-700">
                +£
                {adjustment.earnedRevenue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Lost Revenue:</span>
              <span className="text-red-700">
                -£
                {adjustment.lostRevenue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            {adjustment.refundPenalty > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Refund/Penalty:</span>
                <span className="text-red-700">
                  -£
                  {adjustment.refundPenalty.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
          </div>

          <div className="ml-11 p-2 bg-red-50 border border-red-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-red-700 font-medium">
                Total Lost Revenue:
              </span>
              <span className="text-red-700 font-semibold">
                -£
                {adjustment.netLostRevenue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderFinancingAdjustment = () => {
    if (adjustment.type === "add-loan") {
      const loanTypeLabel =
        adjustment.loanType === "loan"
          ? "Term Loan"
          : adjustment.loanType === "overdraft"
            ? "Overdraft"
            : "Line of Credit";
      const scheduleLabel =
        adjustment.repaymentSchedule === "monthly"
          ? "Monthly"
          : adjustment.repaymentSchedule === "quarterly"
            ? "Quarterly"
            : "Bullet";

      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Plus className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Add {loanTypeLabel} - £
                  {adjustment.amount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.interestRate}% • {adjustment.termMonths} months •{" "}
                  {scheduleLabel} repayment
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Start Date:</span>
              <span>{format(adjustment.startDate, "MMM d, yyyy")}</span>
            </div>
            {adjustment.repaymentSchedule !== "bullet" && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Monthly Payment:</span>
                <span className="text-red-600 font-medium">
                  £
                  {adjustment.monthlyPayment.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Interest:</span>
              <span className="text-orange-600 font-medium">
                £
                {adjustment.totalInterest.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Repayment:</span>
              <span className="font-medium">
                £
                {adjustment.totalRepayment.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "repay-debt") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingDown className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Repay Debt Early - {adjustment.debtName}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  £
                  {adjustment.repaymentAmount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  on {format(adjustment.repaymentDate, "MMM d, yyyy")}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Interest Savings:</span>
              <span className="text-green-600 font-medium">
                £
                {adjustment.interestSavings.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Remaining Balance:</span>
              <span>
                £
                {adjustment.remainingBalance.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    if (adjustment.type === "add-investment") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <DollarSign className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">
                  Investment {getCashMovementTerms().in} - {adjustment.source}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.incomeType === "one-time"
                    ? "One-time payment"
                    : `Recurring ${adjustment.frequency}`}{" "}
                  •{format(adjustment.date, "MMM d, yyyy")}
                </p>
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="ml-11 space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {adjustment.incomeType === "one-time"
                  ? "Amount:"
                  : "Per Payment:"}
              </span>
              <span className="text-green-600 font-medium">
                £
                {adjustment.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                {adjustment.incomeType === "one-time"
                  ? `Total ${getCashMovementTerms().in}:`
                  : `Annual ${getCashMovementTerms().in}:`}
              </span>
              <span className="text-green-600 font-medium">
                £
                {adjustment.annualIncome.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderTransactionAdjustment = () => {
    const isNewTransaction =
      adjustment.type === "add-scenario-transaction" ||
      adjustment.type === "add-revenue" ||
      adjustment.type === "add-cost";
    const isModification =
      adjustment.type === "adjust-transaction" ||
      adjustment.type === "adjust-revenue" ||
      adjustment.type === "adjust-cost";

    if (!isNewTransaction && !isModification) {
      return null;
    }

    const impact = adjustment.netCashImpact ?? adjustment.amount ?? 0;
    const isPositive = impact >= 0;
    const isCost =
      adjustment.type === "add-cost" ||
      adjustment.type === "adjust-cost" ||
      (typeof adjustment.transactionType === "string" &&
        /cost|expense|bill|payment|payable/i.test(adjustment.transactionType));

    const rawFrequency = adjustment.frequency ?? adjustment.recurrence;
    const frequencyLabel =
      typeof rawFrequency === "string" && rawFrequency.length > 0
        ? rawFrequency.charAt(0).toUpperCase() + rawFrequency.slice(1)
        : null;

    const newTransactionDate =
      adjustment.effectiveDate ?? adjustment.date ?? adjustment.expectedDate;
    const modificationDate =
      adjustment.newDueDate ??
      adjustment.originalDueDate ??
      adjustment.effectiveDate;

    return (
      <div className="space-y-2">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                "p-2 rounded-lg",
                isCost ? "bg-blue-100" : "bg-green-100",
              )}
            >
              {isNewTransaction ? (
                <Plus
                  className={cn(
                    "h-4 w-4",
                    isCost ? "text-blue-600" : "text-green-600",
                  )}
                />
              ) : (
                <FileText
                  className={cn(
                    "h-4 w-4",
                    isCost ? "text-blue-600" : "text-green-600",
                  )}
                />
              )}
            </div>
            <div>
              <h4 className="font-semibold text-sm">
                {adjustment.name ||
                  (isNewTransaction
                    ? `New ${isCost ? "Cost" : "Revenue"}`
                    : `Adjust ${isCost ? "Cost" : "Revenue"} Transaction`)}
              </h4>
              {adjustment.description && (
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.description}
                </p>
              )}
              {isNewTransaction && frequencyLabel && (
                <p className="text-xs text-muted-foreground">
                  Frequency: {frequencyLabel}
                </p>
              )}
              {isNewTransaction && newTransactionDate && (
                <p className="text-xs text-muted-foreground">
                  Effective{" "}
                  {format(new Date(newTransactionDate), "MMM d, yyyy")}
                </p>
              )}
              {isModification && modificationDate && (
                <p className="text-xs text-muted-foreground">
                  Effective {format(new Date(modificationDate), "MMM d, yyyy")}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            {onEdit && (
              <Button variant="ghost" size="sm" onClick={onEdit}>
                <Edit2 className="h-3 w-3" />
              </Button>
            )}
            {onRemove && (
              <Button variant="ghost" size="sm" onClick={onRemove}>
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        <div
          className={cn(
            "ml-11 p-2 border rounded text-xs",
            isPositive
              ? "bg-green-50 border-green-200"
              : "bg-red-50 border-red-200",
          )}
        >
          <div className="flex items-center justify-between">
            <span
              className={cn(
                "font-medium",
                isPositive ? "text-green-700" : "text-red-700",
              )}
            >
              {isCost ? "Cost Impact:" : "Revenue Impact:"}
            </span>
            <span
              className={cn(
                "font-semibold",
                isPositive ? "text-green-700" : "text-red-700",
              )}
            >
              {isPositive ? "+" : "-"}£
              {Math.abs(impact).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Generic fallback renderer for any adjustment type not specifically handled
  const renderGenericAdjustment = () => {
    const amount = adjustment.amount || 0;
    const isPositive = amount > 0;

    // win-customer — revenue inflow with date
    if (adjustment.type === "win-customer") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-4 w-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{adjustment.name}</h4>
                {adjustment.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {adjustment.description}
                  </p>
                )}
                {adjustment.details?.customer && (
                  <p className="text-xs text-muted-foreground">
                    Customer: {adjustment.details.customer}
                  </p>
                )}
                {adjustment.details?.paymentTerms && (
                  <p className="text-xs text-muted-foreground">
                    Payment terms: {adjustment.details.paymentTerms} days
                  </p>
                )}
                {adjustment.effectiveDate && (
                  <p className="text-xs text-muted-foreground">
                    Expected:{" "}
                    {format(new Date(adjustment.effectiveDate), "MMM d, yyyy")}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
          <div className="ml-11 p-2 bg-green-50 border border-green-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-green-700 font-medium">
                {getCashMovementTerms().in}:
              </span>
              <span className="text-green-700 font-semibold">
                +£
                {Math.abs(amount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    // hire-employees — recurring payroll cost
    if (adjustment.type === "hire-employees") {
      const monthlyCost = adjustment.details?.monthlyCost ?? Math.abs(amount);
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-4 w-4 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{adjustment.name}</h4>
                {adjustment.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {adjustment.description}
                  </p>
                )}
                {adjustment.details?.department && (
                  <p className="text-xs text-muted-foreground">
                    Department: {adjustment.details.department}
                  </p>
                )}
                {adjustment.details?.period && (
                  <p className="text-xs text-muted-foreground">
                    Period: {adjustment.details.period}
                  </p>
                )}
                {adjustment.effectiveDate && (
                  <p className="text-xs text-muted-foreground">
                    From:{" "}
                    {format(new Date(adjustment.effectiveDate), "MMM d, yyyy")}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
          <div className="ml-11 p-2 bg-red-50 border border-red-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-red-700 font-medium">
                Monthly Payroll Cost:
              </span>
              <span className="text-red-700 font-semibold">
                -£
                {Math.abs(monthlyCost).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                /month
              </span>
            </div>
          </div>
        </div>
      );
    }

    // capital-purchase — one-off capex outflow
    if (adjustment.type === "capital-purchase") {
      return (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Plus className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-sm">{adjustment.name}</h4>
                {adjustment.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {adjustment.description}
                  </p>
                )}
                {adjustment.effectiveDate && (
                  <p className="text-xs text-muted-foreground">
                    Date:{" "}
                    {format(new Date(adjustment.effectiveDate), "MMM d, yyyy")}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              {onEdit && (
                <Button variant="ghost" size="sm" onClick={onEdit}>
                  <Edit2 className="h-3 w-3" />
                </Button>
              )}
              {onRemove && (
                <Button variant="ghost" size="sm" onClick={onRemove}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
          <div className="ml-11 p-2 bg-orange-50 border border-orange-200 rounded text-xs">
            <div className="flex items-center justify-between">
              <span className="text-orange-700 font-medium">
                Capital Expenditure:
              </span>
              <span className="text-orange-700 font-semibold">
                -£
                {Math.abs(amount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        </div>
      );
    }

    const isRevenue =
      adjustment.type?.includes("revenue") || adjustment.type === "add-revenue";
    const isCost =
      adjustment.type?.includes("cost") || adjustment.type?.includes("payment");

    return (
      <div className="space-y-2">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div
              className={cn(
                "p-2 rounded-lg",
                isRevenue
                  ? "bg-green-100"
                  : isCost
                    ? "bg-blue-100"
                    : "bg-gray-100",
              )}
            >
              <FileText
                className={cn(
                  "h-4 w-4",
                  isRevenue
                    ? "text-green-600"
                    : isCost
                      ? "text-blue-600"
                      : "text-gray-600",
                )}
              />
            </div>
            <div>
              <h4 className="font-semibold text-sm">
                {adjustment.name || "Adjustment"}
              </h4>
              {adjustment.description && (
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustment.description}
                </p>
              )}
              {adjustment.date && typeof adjustment.date === "string" && (
                <p className="text-xs text-muted-foreground">
                  {adjustment.date}
                </p>
              )}
              {adjustment.details?.period && (
                <p className="text-xs text-muted-foreground">
                  Period: {adjustment.details.period}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-1">
            {onEdit && (
              <Button variant="ghost" size="sm" onClick={onEdit}>
                <Edit2 className="h-3 w-3" />
              </Button>
            )}
            {onRemove && (
              <Button variant="ghost" size="sm" onClick={onRemove}>
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        {amount !== 0 && (
          <div
            className={cn(
              "ml-11 p-2 border rounded text-xs",
              isPositive
                ? "bg-green-50 border-green-200"
                : "bg-red-50 border-red-200",
            )}
          >
            <div className="flex items-center justify-between">
              <span
                className={cn(
                  "font-medium",
                  isPositive ? "text-green-700" : "text-red-700",
                )}
              >
                {isRevenue
                  ? "Revenue Impact:"
                  : isCost
                    ? "Cost Impact:"
                    : "Impact:"}
              </span>
              <span
                className={cn(
                  "font-semibold",
                  isPositive ? "text-green-700" : "text-red-700",
                )}
              >
                {isPositive ? "+" : ""}£
                {Math.abs(amount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Try specific renderers first, fall back to generic
  try {
    const specificContent =
      renderHeadcountAdjustment() ||
      renderRevenueAdjustment() ||
      renderCostAdjustment() ||
      renderProjectAdjustment() ||
      renderFinancingAdjustment() ||
      renderTransactionAdjustment();

    return (
      <div
        className={cn(
          "p-4 border border-border rounded-lg bg-white hover:shadow-sm transition-shadow",
          className,
        )}
      >
        {specificContent || renderGenericAdjustment()}
      </div>
    );
  } catch (error) {
    console.error("Error rendering adjustment card:", error, adjustment);
    return (
      <div className="p-4 border rounded-lg bg-red-50 border-red-200">
        <p className="text-sm text-red-700">
          Error rendering adjustment: {adjustment?.name || "Unknown"}
        </p>
      </div>
    );
  }
}
